import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogDisableAutomaticBidComponent } from '@app-components/dialog/dialog-disable-automatic-bid/dialog-disable-automatic-bid.component';
import { DialogConfirmSendAlternativeBidComponent } from '@app-components/dialog/dialog-confirm-send-alternative-bid/dialog-confirm-send-alternative-bid.component';
import { DialogReasonCancellationComponent, DialogReasonCancellationData, DialogReasonCancellationResult } from '@app-components/dialog/dialog-reason-cancellation/dialog-reason-cancellation.component';
import { SessionService } from '@app/store/session/session.service';
import { LayoutVars, UserInfo } from '@app/store/session/session.store';
import { Vehicle } from 'src/app/shared/model/vehicle';
import { DealerService, BidStatus, CancelBidReason, ConditionalOffer, AlternativeBidRequest } from 'src/app/shared/services/dealer.service';
import { GaEventsService } from 'src/app/shared/services/ga-events.service';
import { OpenService } from 'src/app/shared/services/open.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'app-make-bid',
  templateUrl: './make-bid.component.html',
  styleUrls: ['./make-bid.component.scss']
})
export class MakeBidComponent implements OnInit {
  @Input() vehicle: Vehicle;
  @Input() disableConfirmationModal: boolean;
  @Input() fromCard: boolean = false;
  @Output() closeParentModal = new EventEmitter<void>();
  @Output() openAlternativeBid = new EventEmitter<void>();

  loading = false;
  bidErrorMessage: string;
  counterOfferErrorMessage: string;
  showLatePaymentLink = false;
  latePaymentQueryParams = { expandedStatuses: '["SOLD_NO_PAYMENT"]' };
  bidValue: number;
  automaticBidValue: number;
  showModal = false;
  user: UserInfo;
  showAutomaticBid: boolean = false;
  layoutVars: LayoutVars;

  isMobile: boolean;

  readonly automaticBidInfo = "Configure um valor máximo para suas ofertas automáticas. Sempre que outro usuário fizer uma oferta superior à sua oferta atual, o sistema aumentará automaticamente sua oferta em incrementos pré-definidos até atingir seu limite máximo."
  readonly defaultOfferInfo = "Caso você confirme esta oferta e outra revenda não faça uma melhor, esse veículo será seu em "
  readonly conditionalOfferInfo = "Caso você confirme esta oferta e nenhuma outra revenda apresente uma proposta melhor, a sua será a única considerada no encerramento do veículo em "
  
  constructor(
    private dealerService: DealerService,
    private eventsService: GaEventsService,
    private openService: OpenService,
    private sessionService: SessionService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService
  ) { }

  ngOnInit() {
    this.bidValue = this.vehicle.currentPrice;
    this.automaticBidValue = this.vehicle.automaticBidValue ? this.vehicle.automaticBidValue : this.bidValue + (this.vehicle.bidStep * 3);
    this.showAutomaticBid = this.vehicle.enabledAutomaticBid;
    this.openService.getLayoutVars().subscribe(layoutVars => this.layoutVars = layoutVars);
    this.sessionService.getUser().subscribe(u => {
      this.user = u;
      if (this.disableConfirmationModal && !this.user?.hasRole('DEALER')) {
        this.showModal = true;
      }
    });

    this.isMobile = window.innerWidth < 768;
  }

  acceptConditionalCounterOffer(): void {
    if (!this.vehicle?.conditionalOffer?.counterOffer) {
      return;
    }
    this.loading = true;
    this.counterOfferErrorMessage = undefined;
    this.dealerService.acceptConditionalCounterOffer(this.vehicle.id, this.vehicle.conditionalOffer as ConditionalOffer)
      .subscribe(resp => {
        if (resp.success) {
          this.openService.getAuctionDetails(this.vehicle.id, false).subscribe(response => {
            Object.assign(this.vehicle, response);
            this.loading = false;
          });
        } else {
          this.counterOfferErrorMessage = BidStatus[resp.status];
          this.loading = false;
        }
      });
  }

  rejectConditionalCounterOffer(): void {
    if (!this.vehicle?.conditionalOffer?.counterOffer) {
      return;
    }
    this.loading = true;
    this.counterOfferErrorMessage = undefined;
    this.dealerService.rejectConditionalCounterOffer(this.vehicle.id, this.vehicle.conditionalOffer as ConditionalOffer)
      .subscribe(resp => {
        if (resp.success) {
          this.openService.getAuctionDetails(this.vehicle.id, false).subscribe(response => {
            Object.assign(this.vehicle, response);
            this.loading = false;
          });
        } else {
          this.counterOfferErrorMessage = BidStatus[resp.status];
          this.loading = false;
        }
      });
  }


  addToBid(mult: number): void {
    this.bidValue += this.vehicle.bidStep * mult;
  }


  makeABid(isAutomaticBid: boolean): void {
    this.bidErrorMessage = undefined;
    this.showLatePaymentLink = undefined;

    if (!isAutomaticBid && this.isBelowMinimumBid()) {
      this.showModal = false;

      if (this.acceptsAlternativeBid()) {
        this.openAlternativeBidModal();
      } else {
        this.bidErrorMessage = 'Este veículo não aceita propostas abaixo do valor mínimo estipulado.';
      }

      return;
    }

    this.loading = true;
    this.dealerService.putABid(this.vehicle.id, this.bidValue, isAutomaticBid, isAutomaticBid ? this.automaticBidValue : 0)
      .subscribe(result => {
        if (result.success) {
          this.vehicle.ownsCurrentBid = true;
          this.vehicle.currentPrice = this.bidValue;
          if (isAutomaticBid) {
            this.vehicle.automaticBidValue = this.automaticBidValue;
          }
          this.vehicle.enabledAutomaticBid = isAutomaticBid;
          this.vehicle.hasBids = true;
          this.vehicle.canCancelBid = false;
          this.vehicle.ownsAnyBid = true;
          this.closeModal();

          if (this.bidValue >= this.vehicle.finishValue) {
            this.vehicle.currentPrice = this.vehicle.finishValue;
            this.vehicle.status = 'SOLD';
            this.openService.getAuctionDetails(this.vehicle.id, false).subscribe(response => {
              this.vehicle.soldAt = response.soldAt;
              this.vehicle.currentPrice = response.currentPrice;
              this.vehicle.status = response.status;
              this.vehicle.bankSlipUrl = response.bankSlipUrl;
            });
            this.eventsService.fireEvent('Arremate', 'Envio', 'vehicleId: ' + this.vehicle.id);
          } else {
            this.eventsService.fireEvent('Lance', 'Envio', 'vehicleId: ' + this.vehicle.id);
          }


        } else {
          this.bidErrorMessage = BidStatus[result.status];
          if (BidStatus[result.status] === BidStatus.LATE_PAYMENT) {
            this.showLatePaymentLink = true;
          }
          this.showModal = false;
        }
        this.loading = false;
      });
  }

  onClickBid(): void {
    if (this.disableConfirmationModal || this.isBelowMinimumBid()) {
      this.makeABid(false);
      return;
    }

    this.clearBidError();
    this.openModal();
  }

  clearBidError(): void {
    this.bidErrorMessage = undefined;
    this.showLatePaymentLink = undefined;
  }

  private acceptsAlternativeBid(): boolean {
    return this.vehicle?.active && !this.vehicle?.hasBids &&this.vehicle?.bidOption !== 'NONE' && !this.vehicle?.ignorePersonalizedOffers;
  }

  private isBelowMinimumBid(): boolean {
    const bidValue = this.bidValue || 0;
    const minimumBid = this.vehicle?.currentPrice || 0;
    return bidValue > 0 && minimumBid > 0 && bidValue < minimumBid;
  }

  private openAlternativeBidModal(): void {
    const alternativeBid = new AlternativeBidRequest();
    alternativeBid.value = this.bidValue;

    this.dialog.open(DialogConfirmSendAlternativeBidComponent, {
      disableClose: false,
      hasBackdrop: true,
      data: {
        vehicle: this.vehicle,
        alternativeBid,
        minimumBidValue: this.vehicle.currentPrice
      }
    })
      .afterClosed()
      .subscribe(result => {
        if (result && result.confirm) {
          this.addAlternativeBid(alternativeBid);
        }
      });
  }

  private addAlternativeBid(alternativeBid: AlternativeBidRequest): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.dealerService.addAlternativeBid(alternativeBid, this.vehicle.id)
      .subscribe(result => {
        if (result.success) {
          this.vehicle.hasPersonalizedOffer = true;
          this.vehicle.personalizedOfferValue = alternativeBid.value;
          this.vehicle.personalizedOfferStatus = 'NEW';
          this.closeModal();
          this.toastr.success('Proposta personalizada enviada com sucesso!');
          this.eventsService.fireEvent('Proposta personalizada', 'Envio', 'vehicleId: ' + this.vehicle.id);
        } else {
          this.bidErrorMessage = BidStatus[result.status];
          if (BidStatus[result.status] === BidStatus.LATE_PAYMENT) {
            this.showLatePaymentLink = true;
          }
        }
        this.loading = false;
      });
  }

  openModal(): void {
    this.clearBidError();
    this.showModal = true;
  }

  closeModal(): void {
    if (this.disableConfirmationModal && !this.user?.hasRole('DEALER')) {
      this.showModal = false;
    } else {
      this.showModal = false;
    }
    this.closeParentModal.emit();
  }

  triggerAlternativeBid(): void {
    this.openAlternativeBid.emit();
  }

  openCancelBidModal(): void {
    const dialogRef = this.dialog.open(DialogReasonCancellationComponent, {
      width: '520px',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'dialog-cancel-auction',
      data: {
        vehicle: this.vehicle,
        isAdmin: false,
        canCancelSell: false,
        title: 'Deseja cancelar a oferta?',
        description: 'Ao confirmar, esta oferta será cancelada e você não irá mais disputar a compra deste veículo. Esta ação não pode ser desfeita.',
        closeLabel: 'Manter oferta ativa',
        reasons: Object.entries(CancelBidReason).map(([value, label]) => ({ value, label })),
      } satisfies DialogReasonCancellationData,
    });

    dialogRef.afterClosed().subscribe((result: DialogReasonCancellationResult | null) => {
      if (result) {
        this.cancelBid(result.cancellationReason, result.cancellationNote);
      }
    });
  }

  canBid(): boolean {
    return this.user?.hasRole('DEALER');
  }

  cancelBid(reason: string, cancellationNote?: string): void {
    this.loading = true;
    this.dealerService.cancelBid(this.vehicle, reason, cancellationNote)
      .subscribe(result => {
        if (result.success) {
          this.vehicle.ownsCurrentBid = false;

          this.openService.getAuctionDetails(this.vehicle.id, false).subscribe(response => {
            this.vehicle = response;
            this.loading = false;
          });


        } else {
          this.bidErrorMessage = BidStatus[result.status];
          this.loading = false;
        }
      });

  }

  disableAutomaticBid(event: any): void {
    const checked = (event.target as HTMLInputElement).checked;
    event.preventDefault();
    event.stopPropagation();

    if (!checked) {
      this.dialog.open(DialogDisableAutomaticBidComponent, { disableClose: false, hasBackdrop: true })
        .afterClosed().subscribe(result => {
          if (result && result.disable) {
            this.dealerService.cancelAutomaticBid(this.vehicle.id, this.vehicle.automaticBidValue).subscribe((resp) => {
              if (resp.success) {
                this.vehicle.enabledAutomaticBid = false;
                (event.target as HTMLInputElement).checked = false;
              }
            });
          } else {
            this.vehicle.enabledAutomaticBid = true;
          }
        });
    }
  }

  calculateCommissionValue(): number {
    if (!this.vehicle.comissionValueConfig) {
      return 0;
    }

    const bidValue = this.bidValue || this.vehicle.currentPrice || 0;
    const platesQtd = 1 + (this.vehicle.additionalPlatesQtd || 0);
    
    switch (this.vehicle.comissionValueConfig.type) {
      case 'PERCENTAGE':
        return (bidValue * this.vehicle.comissionValueConfig.value) / 100;
      case 'REAL':
        return (this.vehicle.comissionValueConfig.value || 0) * platesQtd;
      case 'REAL_AND_PERCENTAGE':
        const percentageValue = (bidValue * this.vehicle.comissionValueConfig.value) / 100;
        const additionalFee = (this.vehicle.comissionValueConfig.additionalFee || 0) * platesQtd;
        return additionalFee + percentageValue;
      default:
        return 0;
    }
  }

  calculateTotalOperation(): number {
    const bidValue = this.bidValue || this.vehicle.currentPrice || 0;
    return bidValue + this.calculateCommissionValue();
  }
}
